import React, { useState, useEffect, useCallback } from 'react';
import { ChatKit, useChatKit } from '@openai/chatkit-react';
import { motion } from 'framer-motion';
import './CIOReport.css';

const CIOReport = ({ onBack }) => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Force a test API call on mount
  useEffect(() => {
    console.log('CIOReport mounted - testing API');
    fetch('/api/chatkit/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    })
    .then(res => {
      console.log('API test response:', res.status);
      return res.json();
    })
    .then(data => {
      console.log('API test data:', data);
      if (data && data.client_secret) {
        console.log('Client secret received:', data.client_secret.substring(0, 20) + '...');
      } else {
        console.log('No client_secret in response');
      }
    })
    .catch(err => console.error('API test error:', err));
  }, []);

  // Create getClientSecret function using useCallback to prevent re-renders
  const getClientSecret = useCallback(async (existing) => {
    console.log('getClientSecret called with existing:', existing);
    try {
      if (existing) {
        const refreshRes = await fetch('/api/chatkit/refresh', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ existingSecret: existing }),
        });
        
        if (!refreshRes.ok) {
          throw new Error('Failed to refresh session');
        }
        
        const { client_secret } = await refreshRes.json();
        return client_secret;
      }
      
      // Get new session token
      console.log('Creating ChatKit session...');
      const res = await fetch('/api/chatkit/session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });
      console.log('Session response status:', res.status);
      
      if (!res.ok) {
        const errorData = await res.json();
        console.error('Session creation failed:', errorData);
        throw new Error(errorData.error || 'Failed to create session');
      }
      
      const data = await res.json();
      console.log('Session created:', data);
      if (!data.client_secret) {
        throw new Error('No client_secret received');
      }
      // Set loading false since we have a valid token
      setLoading(false);
      return data.client_secret;
    } catch (err) {
      console.error('ChatKit error:', err);
      setError(err.message);
      setLoading(false);
      throw err;
    }
  }, []);

  // Initialize ChatKit with your agent configuration
  const chatKitConfig = {
    api: {
      getClientSecret,
    },
    onError: (error) => {
      console.error('ChatKit initialization error:', error);
      setError('ChatKit failed to initialize');
      setLoading(false);
    },
    onSuccess: () => {
      console.log('ChatKit initialized successfully');
      setLoading(false);
      setError(null);
    },
  };
  
  console.log('Initializing ChatKit with config');
  const { control, error: chatKitError } = useChatKit(chatKitConfig);
  
  // Log control changes
  useEffect(() => {
    console.log('ChatKit control:', control);
  }, [control]);

  useEffect(() => {
    if (chatKitError) {
      console.error('ChatKit hook error:', chatKitError);
      setError(chatKitError.message || 'ChatKit initialization failed');
      setLoading(false);
    }
  }, [chatKitError]);

  // Remove timeout since initialization is working but onSuccess might not fire
  // The loading state is now managed in getClientSecret

  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1, 
      transition: { duration: 0.4 } 
    }
  };

  return (
    <motion.div 
      className="cio-report-container"
      initial="hidden"
      animate="visible"
      variants={fadeIn}
    >
      <div className="cio-report-header">
        <button className="back-button" onClick={onBack}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back to Dashboard
        </button>
        <h1>CIO Report</h1>
      </div>

      <div className="cio-report-content">
        {loading && !control && (
          <div className="loading-container">
            <div className="loader"></div>
            <p>Initializing CIO Assistant...</p>
          </div>
        )}
        
        {error && (
          <div className="error-container">
            <p className="error-message">Error: {error}</p>
            <p className="error-hint">Check the browser console for detailed error messages.</p>
            <p className="error-hint">Domain: {window.location.hostname}</p>
          </div>
        )}
        
        {control && !error && (
          <div className="chat-container">
            <ChatKit 
              control={control} 
              className="chatkit-wrapper"
              theme={{
                // Customize theme to match your app
                mode: 'dark',
                primaryColor: '#3b82f6',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              }}
              initialMessages={[
                {
                  role: 'assistant',
                  content: 'Hello! I\'m your CIO assistant. How can I help you today?'
                }
              ]}
            />
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default CIOReport;